import { ogImageMeta, hubOgKey, bestOgKey } from "@/lib/og-images";
import { createFileRoute, Link } from "@tanstack/react-router";
import { fetchCategoryHub } from "@/lib/library-hubs.functions";
import { subcategoryIntro, HUB_MIN_ITEMS } from "@/lib/library-hubs";
import {
  LibraryHubPage,
  itemListJsonLd,
  breadcrumbJsonLd,
  datasetJsonLd,
} from "@/components/library-hub";
import { SiteFooter } from "@/components/site-footer";

export const Route = createFileRoute("/library/c/$category/$sub")({
  loader: async ({ params }) => {
    try {
      const hub = await fetchCategoryHub({
        data: { category: params.category, subcategory: params.sub },
      });
      return { hub };
    } catch {
      return { hub: null };
    }
  },
  head: ({ params, loaderData }) => {
    const hub = loaderData?.hub ?? null;
    const url = `https://vibing.inc/library/c/${params.category}/${params.sub}`;
    if (!hub || !hub.subcategory) {
      return {
        meta: [
          { title: "Collection not found — The Vibeary" },
          { name: "robots", content: "noindex" },
        ],
        links: [{ rel: "canonical", href: url }],
      };
    }
    const title = `${hub.subcategory} — ${hub.count} picks | The Vibeary`;
    const desc = subcategoryIntro(
      hub.subcategory,
      hub.category,
      hub.count,
      hub.gems.map((g) => g.name),
    ).slice(0, 155);
    return {
      meta: [
        { title },
        { name: "description", content: desc },
        { property: "og:title", content: title },
        { property: "og:description", content: desc },
        { property: "og:type", content: "website" },
        { property: "og:url", content: url },
        ...ogImageMeta(hubOgKey(params.category), `Share card: ${hub.subcategory} picks in the Hidden Gems Library`),
        { name: "twitter:card", content: "summary_large_image" },
        ...(hub.count < HUB_MIN_ITEMS
          ? [{ name: "robots", content: "noindex,follow" }]
          : []),
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: [
        {
          type: "application/ld+json",
          children: itemListJsonLd(hub.subcategory, url, hub.gems),
        },
        {
          type: "application/ld+json",
          children: datasetJsonLd({
            name: `${hub.subcategory} — The Vibing Library`,
            description: desc,
            url,
            count: hub.count,
            keywords: [hub.subcategory, hub.category, "tools", "curated"],
          }),
        },
        {
          type: "application/ld+json",
          children: breadcrumbJsonLd([
            { name: "Home", item: "https://vibing.inc/" },
            { name: "The Vibeary", item: "https://vibing.inc/library" },
            {
              name: hub.category,
              item: `https://vibing.inc/library/c/${params.category}`,
            },
            { name: hub.subcategory, item: url },
          ]),
        },
      ],
    };
  },
  component: SubcategoryHub,
});

function SubcategoryHub() {
  const { hub } = Route.useLoaderData();
  const { category, sub } = Route.useParams();

  if (!hub || !hub.subcategory) {
    return (
      <div className="min-h-dvh bg-background text-foreground">
        <main id="main" className="mx-auto max-w-3xl px-6 py-24 text-center">
          <h1 className="font-display text-3xl">Collection not found</h1>
          <Link to="/library" className="mt-4 inline-block text-muted-foreground underline">
            Back to the library
          </Link>
        </main>
        <SiteFooter />
      </div>
    );
  }

  return (
    <LibraryHubPage
      eyebrow={hub.category}
      title={hub.subcategory}
      intro={subcategoryIntro(
        hub.subcategory,
        hub.category,
        hub.count,
        hub.gems.map((g) => g.name),
      )}
      gems={hub.gems}
      crumbs={[
        { label: "Home", to: "/" },
        { label: "The Vibeary", to: "/library" },
        { label: hub.category, to: `/library/c/${category}` },
        { label: hub.subcategory },
      ]}
      relatedTitle={`More in ${hub.category}`}
      related={hub.siblings
        .filter((s) => s.count >= HUB_MIN_ITEMS && s.slug !== sub)
        .map((s) => ({
          label: s.name,
          href: `/library/c/${category}/${s.slug}`,
          count: s.count,
        }))}
    />
  );
}
